import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { GetProductCollectionApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import {
    getErrorMessage,
    getProductInfoFromList,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    subtitle = "subtitle",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    productNumbers = "productNumbers",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    maxNumberOfColumns = "maxNumberOfColumns",
    buttonLabel = "buttonLabel",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subtitle]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.productNumbers]: string[];
        [fields.numberOfProductsToDisplay]: number;
        [fields.maxNumberOfColumns]: number;
        [fields.buttonLabel]: string;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    products: getProductsForProductInfoList(state, ownProps.id),
    errorMessage: getErrorMessage(state, ownProps.id),
});

const mapDispatchToProps = {
    loadCarouselProducts,
    loadProductInfoList,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductSliderStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    subtitleText?: TypographyPresentationProps;
    errorMessageText?: TypographyPresentationProps;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productNameLink?: LinkPresentationProps;
    asLowAsText?: TypographyPresentationProps;
    priceWrapper?: InjectableCss;
    viewProductButton?: ClickablePresentationProps;
}

export const productSliderStyles: ProductSliderStyles = {
    wrapper: {
        css: css`
            padding: 40px 0;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            text-transform: uppercase;
            font-weight: 700;
            font-size: 24px;
            margin-bottom: 8px;
        `,
    },
    subtitleText: {
        css: css`
            text-align: center;
            font-size: 15px;
            margin: 0 auto 36px auto;
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    mainContainer: {
        gap: 0,
        isFullWidth: true,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            flex-basis: 4%;
            max-width: 4%;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            background: transparent;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            flex-basis: 4%;
            max-width: 4%;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            background: transparent;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            flex-basis: 92%;
            max-width: 92%;
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 4%;
            display: flex;
            flex-direction: column;
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                justify-content: center;
                min-height: 170px;
                width: 100%;
            `,
        },
        img: {
            css: css`
                width: auto;
                max-width: 100%;
                height: auto;
                max-height: 165px;
            `,
        },
    },
    productNameLink: {
        css: css`
            margin-top: 16px;
            width: 100%;
        `,
        color: "text.main",
        typographyProps: {
            size: 13,
            weight: 600,
        },
    },
    asLowAsText: {
        css: css`
            font-weight: 700;
            font-size: 14px;
            margin: auto 0 0 0;
            padding-top: 12px;
        `,
    },
    priceWrapper: {
        css: css`
            font-weight: 700;
            font-size: 18px;
        `,
    },
    viewProductButton: {
        css: css`
            margin-top: 16px;
            background: #ffd100;
            color: #000;
            font-weight: 800;
            font-size: 15px;
            line-height: 15px;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            text-decoration: none;
            justify-content: center;
            padding: 24px;
            width: 100%;
            border-radius: 0;
            &:hover {
                background: #e6bc00;
            }
        `,
    },
};

const styles = productSliderStyles;

interface ProductSliderCardOwnProps {
    sliderId: string;
    product: ProductModel;
    buttonLabel: string;
}

const cardMapStateToProps = (state: ApplicationState, ownProps: ProductSliderCardOwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.sliderId, ownProps.product.id),
});

const cardMapDispatchToProps = {
    updateProductInfo,
};

type ProductSliderCardProps = ProductSliderCardOwnProps &
    ReturnType<typeof cardMapStateToProps> &
    ResolveThunks<typeof cardMapDispatchToProps>;

const ProductSliderCardView = ({
    sliderId,
    product,
    buttonLabel,
    productInfo,
    updateProductInfo,
}: ProductSliderCardProps) => {
    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: (unitOfMeasure: string) =>
            updateProductInfo({ id: sliderId, productId: product.id, unitOfMeasure }),
        onQtyOrderedChanged: (qtyOrdered: number) =>
            updateProductInfo({ id: sliderId, productId: product.id, qtyOrdered }),
    };

    return (
        <ProductContext.Provider value={productContext}>
            <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
            <Link href={product.canonicalUrl} {...styles.productNameLink}>
                {product.productTitle}
            </Link>
            <Typography as="p" {...styles.asLowAsText}>
                {translate("As low as")}
            </Typography>
            <StyledWrapper {...styles.priceWrapper}>
                <ProductPrice showLabel={false} product={productContext} />
            </StyledWrapper>
            <Clickable
                href={product.canonicalUrl}
                {...styles.viewProductButton}
                data-test-selector={`productSliderViewProduct_${product.id}`}
            >
                {buttonLabel || translate("View Product")}
            </Clickable>
        </ProductContext.Provider>
    );
};

const ProductSliderCard = connect(cardMapStateToProps, cardMapDispatchToProps)(ProductSliderCardView);

/**
 * Custom styled product slider per the SL31 Industries designs.
 * Displays top sellers from selected categories with an "As low as" price and a View Product button.
 */
const ProductSlider = ({
    id,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    loadProductInfoList,
    theme,
    shellContext,
}: Props) => {
    const manualProductNumbers = (fields.productNumbers || []).map(o => o.trim()).filter(o => o.length > 0);

    useEffect(() => {
        if (fields.displayProductsFrom === "manual") {
            if (manualProductNumbers.length === 0) {
                return;
            }

            const getProductCollectionParameter: GetProductCollectionApiV2Parameter = {
                productNumbers: manualProductNumbers,
                pageSize: manualProductNumbers.length,
                expand: ["images", "badges"],
            };

            loadProductInfoList({
                id,
                getProductCollectionParameter,
                // the products API does not guarantee request order — restore the CMS-entered order
                additionalSort: loadedProducts =>
                    manualProductNumbers
                        .map(productNumber =>
                            loadedProducts.find(p => p.productNumber?.toLowerCase() === productNumber.toLowerCase()),
                        )
                        .filter((p): p is ProductModel => !!p),
                isShellEdit: shellContext.isInShell && shellContext.isEditing,
            });
            return;
        }

        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType: "topSellers",
            relatedProductType: "",
            seedWithManuallyAssigned: "",
            displayProductsFrom: fields.displayProductsFrom || "selectedCategories",
            selectedCategoryIds: fields.selectedCategoryIds,
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            widgetPosition: 0,
            isProductDetailsPage: false,
            productId: undefined,
            isProductListPage: false,
            category: undefined,
            isBrandDetailsPage: false,
            brand: undefined,
        });
    }, [
        fields.displayProductsFrom,
        fields.selectedCategoryIds,
        fields.numberOfProductsToDisplay,
        manualProductNumbers.join("|"),
    ]);

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const handleCarouselKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            embla && embla.scrollPrev();
        } else if (event.key === "ArrowRight") {
            event.preventDefault();
            embla && embla.scrollNext();
        }
    };

    const getSlidesToShow = useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 6;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToShow: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToShow = 2;
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToShow = 3;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToShow = 4;
        } else {
            localSlidesToShow = maxNumberOfColumns;
        }
        return Math.min(localSlidesToShow, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToShow, setSlidesToShow] = useState(0);

    useEffect(() => {
        setSlidesToShow(getSlidesToShow());
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || !products || products.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            // show a group of products at once, but arrows advance a single product at a time
            slidesToScroll: 1,
            watchDrag: isTouchDevice(),
            loop: products.length > slidesToShow,
        });
        setCanScroll();
    }, [embla, slidesToShow, products]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load slider products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!products || products.length === 0) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${fields.customCSS}
        `,
    };

    const showArrows = products.length > slidesToShow;

    return (
        <StyledWrapper {...customCssWrapper} data-test-selector="productSlider">
            {fields.title && <Typography {...styles.titleText}>{fields.title}</Typography>}
            {fields.subtitle && (
                <Typography as="p" {...styles.subtitleText}>
                    {fields.subtitle}
                </Typography>
            )}
            <GridContainer {...styles.mainContainer}>
                <GridItem {...styles.prevArrowGridItem}>
                    {showArrows && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="productSliderPrevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    )}
                </GridItem>
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper
                        {...styles.carouselWrapper}
                        tabIndex={0}
                        role="region"
                        aria-label={translate("Product slider")}
                        onKeyDown={handleCarouselKeyDown}
                    >
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {products.map(product => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={product.id}
                                        style={{ width: `calc(100% / ${slidesToShow})` }}
                                    >
                                        <StyledWrapper {...styles.carouselSlideInner}>
                                            <ProductSliderCard
                                                sliderId={id}
                                                product={product}
                                                buttonLabel={fields.buttonLabel}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.nextArrowGridItem}>
                    {showArrows && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="productSliderNextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    )}
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ProductSlider))),
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        displayName: "Product Slider",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Most Popular Products",
                fieldType: "Translatable",
                sortOrder: 0,
                tab: basicTab,
            },
            {
                name: fields.subtitle,
                displayName: "Subtitle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Selected Categories", value: "selectedCategories" },
                    { displayName: "All Categories", value: "allCategories" },
                    { displayName: "Entered Product Numbers", value: "manual" },
                ],
                hideEmptyOption: true,
                defaultValue: "selectedCategories",
                fieldType: "General",
                sortOrder: 2,
                tab: basicTab,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 3,
                tab: basicTab,
                isVisible: item => item.fields.displayProductsFrom === "selectedCategories",
            },
            {
                name: fields.productNumbers,
                displayName: "Product Numbers",
                editorTemplate: "TagsField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 3,
                tab: basicTab,
                isVisible: item => item.fields.displayProductsFrom === "manual",
                tooltip:
                    "Type a product number and press Enter to add it. Products display in the order they are entered.",
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number of Products to Display",
                editorTemplate: "IntegerField",
                defaultValue: 12,
                min: 1,
                max: 24,
                fieldType: "General",
                sortOrder: 4,
                tab: basicTab,
                isVisible: item => item.fields.displayProductsFrom !== "manual",
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Products Visible at Once",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "4", value: 4 },
                    { displayName: "5", value: 5 },
                    { displayName: "6", value: 6 },
                ],
                hideEmptyOption: true,
                defaultValue: 6,
                fieldType: "General",
                sortOrder: 5,
                tab: basicTab,
            },
            {
                name: fields.buttonLabel,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "View Product",
                fieldType: "Translatable",
                sortOrder: 6,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: "",
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            },
        ],
    },
};

export default widgetModule;
